<template>
  <TableList
    v-rect-loading="loading"
    noAction
    class="applicant-status-notification-template-config-list"
    :title="$t(`${i18nPrefix}.title`)"
    :listData="configList"
    :tableColumns="tableColumns"
  >
    <template #filter>
      <el-select
        v-model="search.companyId"
        filterable
        class="company-filter"
        @change="searchCompanyList"
      >
        <el-option
          v-for="item in companyList"
          :key="item.id"
          :style="{ paddingLeft: item.level * 15 + 'px' }"
          :label="`${item.id} - ${item.i18nName}`"
          :value="item.id"
        />
      </el-select>
    </template>
    <template #btn>
      <el-button v-show="isSave" class="create-group-btn create-btn" @click="saveConfig">{{ $t(`${i18nPrefix}.btn.saveConfig`) }}</el-button>
      <el-button v-show="isAdd" class="create-group-btn create-btn" @click="addConfig">{{ $t(`${i18nPrefix}.btn.addConfig`) }}</el-button>
      <el-button v-show="isAddAndSave" class="create-group-btn create-btn" @click="saveConfig">{{ $t(`${i18nPrefix}.btn.saveConfig`) }}</el-button>
    </template>
    <template #emailTemplateToEmployee="{ row }">
      <el-select
        v-model="row.to_employee_email_template_id"
        clearable
        size="medium"
        :placeholder="$t(`${i18nPrefix}.placeholder.emailTemplateTo`)"
      >
        <el-option
          v-for="(emailTemplate, index) in toEmployeeEmailTemplateList"
          :key="index"
          :label="emailTemplate.name"
          :value="emailTemplate.id"
        ></el-option>
      </el-select>
    </template>
    <template #emailTemplateTo="{ row }">
      <el-select
        v-model="row.email_template_id"
        clearable
        size="medium"
        :placeholder="$t(`${i18nPrefix}.placeholder.emailTemplateTo`)"
      >
        <el-option
          v-for="(emailTemplate, index) in toCandidateEmailTemplateList"
          :key="index"
          :label="emailTemplate.name"
          :value="emailTemplate.id"
        ></el-option>
      </el-select>
    </template>
    <template #emailTemplateToReferrer="{ row }">
      <el-select
        v-model="row.to_referrer_email_template_id"
        clearable
        size="medium"
        :placeholder="$t(`${i18nPrefix}.placeholder.emailTemplateTo`)"
      >
        <el-option
          v-for="(emailTemplate, index) in toReferrerEmailTemplateList"
          :key="index"
          :label="emailTemplate.name"
          :value="emailTemplate.id"
        ></el-option>
      </el-select>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'
import TableList from '@/components/tableList/allowDragSortTable.vue'

export default {
  components: {
    TableList
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.applicantStatusNotificationTemplate',
      companyList: [],
      company: null,
      configList: [],
      toCandidateEmailTemplateList: [],
      toReferrerEmailTemplateList: [],
      search: {
        companyId: null
      },
      isSave: true,
      isAdd: true,
      isAddAndSave: false
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          width: 60,
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'i18nName'
        },
        {
          width: 60,
          label: this.$t(`${this.i18nPrefix}.label.emailTemplateToEmployeeCandidateName`),
          dataKey: 'email_template_to_employee',
          slotName: 'emailTemplateToEmployee'
        },
        {
          width: 60,
          label: this.$t(`${this.i18nPrefix}.label.emailTemplateToCandidateName`),
          dataKey: 'email_template_to',
          slotName: 'emailTemplateTo'
        },
        {
          width: 60,
          label: this.$t(`${this.i18nPrefix}.label.emailTemplateToReferrerName`),
          dataKey: 'email_template_to_referrer',
          slotName: 'emailTemplateToReferrer'
        }
      ]
      return columns
    }
  },
  created () {
    this.getCompanyList()
  },
  methods: {
    ...mapActions('applicantStatusNotificationTemplateConfig', [
      'getCompanyApplicantStatusNotificationTemplateConfig',
      'updateApplicantStatusNotificationTemplateConfig',
      'createApplicantStatusNotificationTemplateConfig',
      'getCompanyApplicantStatusEmailTemplateList'
    ]),
    ...mapActions('company', ['getCompanyChildrenTree']),
    flatCompanyChildrenTreeMap (resultList, company, treeMap) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      resultList.push(company)
      _.forEach(treeMap[company.id], child => {
        this.flatCompanyChildrenTreeMap(resultList, child, treeMap)
      })
    },
    async getCompanyList (companyId) {
      this.loading = true
      try {
        const resp = await this.getCompanyChildrenTree()
        if (resp) {
          const currentCompany = resp.self
          const companyList = []
          this.flatCompanyChildrenTreeMap(companyList, currentCompany, resp.children)
          this.companyList = companyList
          this.company = currentCompany
        }
        if (!companyId) {
          this.search.companyId = this.company.id
        } else {
          this.search.companyId = companyId
        }
        await this.searchCompanyList()
      } finally {
        this.loading = false
      }
    },
    async searchCompanyList () {
      this.loading = true
      try {
        if (!this.search.companyId) {
          return
        }
        const resp = await this.getCompanyApplicantStatusNotificationTemplateConfig({ companyId: this.search.companyId })
        if (resp) {
          const configList = resp.applicant_status_config
          this.configList = _.map(configList, config => {
            addI18nProperty(config, 'i18nName', 'name_cn', 'name_en')
            return config
          })
          this.isSave = true
          this.isAdd = false
        }
        this.getApplicantStatusTemplateList(this.search.companyId)
      } finally {
        this.loading = false
      }
    },
    async getApplicantStatusTemplateList (companyId) {
      const resp = await this.getCompanyApplicantStatusEmailTemplateList(companyId)
      if (resp) {
        this.toCandidateEmailTemplateList = resp.to_candidate_email_template_list
        this.toReferrerEmailTemplateList = resp.to_referrer_email_template_list
        this.toEmployeeEmailTemplateList = resp.to_employee_email_template_list
      }
    },
    async addConfig () {
      this.loading = true
      try {
        if (!this.search.companyId) {
          return
        }
        const resp = await this.getCompanyApplicantStatusNotificationTemplateConfig({ companyId: this.search.companyId })
        if (resp) {
          this.configList = _.map(resp.applicant_status_config, config => {
            addI18nProperty(config, 'i18nName', 'name_cn', 'name_en')
            return config
          })
        }
        this.isAdd = false
        this.isSave = false
        this.isAddAndSave = true
        this.getApplicantStatusTemplateList(this.search.companyId)
      } finally {
        this.loading = false
      }
    },
    async saveConfig () {
      if (!this.search.companyId) {
        return
      }
      this.loading = true
      let success = false
      try {
        const configList = this.configList
        const saveFunc = this.isAddAndSave ? this.createApplicantStatusNotificationTemplateConfig : this.updateApplicantStatusNotificationTemplateConfig
        const resp = await saveFunc({ companyId: this.search.companyId, configList })
        if (resp) {
          success = true
          this.isAddAndSave = false
        }
        await this.getCompanyList(this.search.companyId)
      } finally {
        this.loading = false
        if (success) {
          this.$message.success(this.$t('common.message.saveSuccess'))
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.applicant-status-notification-template-config-list {
  .filter-group {
    .company-filter {
      width: 50%;
    }
  }

  .tip-message-icon {
    margin-left: 5px;
    font-size: 14px;
  }
}
</style>
